import { Fragment, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { Skeleton, TextFieldProps, TextFieldVariants } from '@mui/material';
import { IValidationResult, usePerformanceReportBuilderPageStore } from '@diana/core';
import { useUpdateLayoutNameMutation } from '../Mutations/useUpdateLayoutNameMutation';
import { PERFORMANCE_REPORT_BUILDER_PAGE_NS } from '../i18n';
import { usePerformanceReportBuilderPageContext } from '../PerformanceReportBuilderPageContext';
import { TextFieldWithValidationTooltip } from '../../FlowBuilder2Page/Components/TextFieldWithValidationTooltip/TextFieldWithValidationTooltip';

export const LayoutNameField = <V extends TextFieldVariants>(
  props: TextFieldProps<V> & { autoWidth?: boolean; fontSize?: string },
) => {
  const { autoWidth, onChange, onBlur, ...textFieldProps } = props;

  const { t } = useTranslation(PERFORMANCE_REPORT_BUILDER_PAGE_NS);

  const { isLoading, error } = usePerformanceReportBuilderPageContext();

  const layoutExists = usePerformanceReportBuilderPageStore((s) => !!s.layout);
  const layoutDisplayName = usePerformanceReportBuilderPageStore(
    (s) => s.layout?.displayName ?? '',
  );

  const [value, setValue] = useState<string>(layoutDisplayName);
  const [touched, setTouched] = useState<boolean>(false);

  const { mutate } = useUpdateLayoutNameMutation();
  const updateINameDebounce = useDebouncedCallback(mutate, 1500);

  const validationResult = useMemo<IValidationResult | null>(() => {
    if (touched && !value) {
      return {
        type: 'error',
        message: t('errors.dashboardNameIsRequired'),
      };
    }
    return null;
  }, [t, touched, value]);

  useLayoutEffect(() => {
    setValue(layoutDisplayName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutDisplayName]);

  if (isLoading) return <Skeleton variant="rounded" width={150} height={15} />;

  if (!error && layoutExists)
    return (
      <TextFieldWithValidationTooltip
        data-testid="LayoutNameTextField"
        placeholder={t('placeholders.untitledDashboard')}
        autoFocus={!layoutDisplayName}
        autoWidth={autoWidth}
        error={validationResult?.type === 'error'}
        validationResult={validationResult}
        {...textFieldProps}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          updateINameDebounce(e.target.value);
          onChange?.(e);
        }}
        onBlur={(e) => {
          onBlur?.(e);
          setTouched(true);
        }}
      />
    );

  return <Fragment />;
};
